import React, { useEffect, useMemo, useState, useCallback } from 'react';
import './CareersPage.css';
import { getImageUrl } from './utils/mediaUtils';
import { getAPIBase } from './config';
import {
  Briefcase,
  Calendar,
  MapPin,
  ArrowRight,
  CheckCircle2,
  Star,
  X,
  Mail,
  Send,
  Heart,
  GraduationCap,
  Clock,
  Sparkles,
  Hourglass,
  Handshake,
} from 'lucide-react';

const API_BASE = (() => {
  if (process.env.REACT_APP_API_BASE) return process.env.REACT_APP_API_BASE;
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') {
    const { hostname, protocol } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'https://permataharapansecondary.sch.id/api';
    }
    return `${protocol}//${hostname}/api`;
  }
  return 'https://permataharapansecondary.sch.id/api';
})();

const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

const isVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const lower = url.toLowerCase();
  return (
    lower.endsWith('.mp4') ||
    lower.endsWith('.webm') ||
    lower.endsWith('.ogg') ||
    lower.includes('youtube.com') ||
    lower.includes('youtu.be')
  );
};

const extractYouTubeId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  return m ? m[1] : null;
};

function CareersPage({ page }) {
  // 1. Media Map from CMS
  const [mediaMap, setMediaMap] = useState(() => {
    try {
      const cached = sessionStorage.getItem('cms_media_homepage') || sessionStorage.getItem('cms_media_map');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  });

  // 2. Published Live Sections from Database (content_published)
  const [liveSections, setLiveSections] = useState(() => {
    try {
      const cached = sessionStorage.getItem('cms_page_careers');
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  });
  const [loading, setLoading] = useState(!liveSections);

  const [activeSlide, setActiveSlide] = useState(0);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);

  // Load media library map from CMS for resolving ID/filename references
  useEffect(() => {
    let isMounted = true;
    const loadMedia = async () => {
      try {
        const apiBase = getAPIBase ? getAPIBase() : API_BASE;
        const res = await fetch(`${apiBase}/media?page=careers&_t=${Date.now()}`, {
          credentials: 'omit',
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (res.ok) {
          const json = await res.json();
          const mediaList = json.data?.items || json.data || [];
          const map = {};
          mediaList.forEach((m) => {
            const mId = m.id || m.media_id;
            let mUrl = m.url || m.file_path;
            if (mUrl && !mUrl.startsWith('http://') && !mUrl.startsWith('https://')) {
              mUrl = `${API_ORIGIN}${mUrl.startsWith('/') ? '' : '/'}${mUrl}`;
            }
            if (mId && mUrl) {
              map[mId] = mUrl;
              map[String(mId)] = mUrl;
              const num = Number(mId);
              if (!isNaN(num)) map[num] = mUrl;
            }
          });
          if (isMounted && Object.keys(map).length > 0) {
            setMediaMap(map);
            try {
              sessionStorage.setItem('cms_media_homepage', JSON.stringify(map));
            } catch (_) {}
          }
        }
      } catch (e) {
        console.warn('[CareersPage] Media fetch error:', e);
      }
    };
    loadMedia();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch published sections directly from database (content_published)
  const fetchPublishedSections = useCallback(async () => {
    try {
      const apiBase = getAPIBase ? getAPIBase() : API_BASE;
      const res = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=careers&_t=${Date.now()}`, {
        credentials: 'omit',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
      });
      if (res.ok) {
        const json = await res.json();
        const sections = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        if (sections.length > 0) {
          const sectionMap = {};
          sections.forEach((s) => {
            const cp =
              s.content_published && s.content_published !== '{}' && s.content_published !== '[]'
                ? s.content_published
                : s.content || s.content_published;
            let parsed = null;
            try {
              parsed = typeof cp === 'string' ? JSON.parse(cp) : cp;
            } catch (e) {
              parsed = cp;
            }
            if (parsed) {
              sectionMap[s.section_type] = parsed.en || parsed;
            }
          });
          if (Object.keys(sectionMap).length > 0) {
            setLiveSections(sectionMap);
            try {
              sessionStorage.setItem('cms_page_careers', JSON.stringify(sectionMap));
            } catch (_) {}
          }
        }
      }
    } catch (err) {
      console.warn('[CareersPage] Error fetching published careers sections:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPublishedSections();

    const handleUpdate = () => {
      fetchPublishedSections();
    };
    window.addEventListener('cms-careers-updated', handleUpdate);
    return () => {
      window.removeEventListener('cms-careers-updated', handleUpdate);
    };
  }, [fetchPublishedSections]);

  // Robust media resolver: mediaMap -> getImageUrl -> absolute path fallback
  const resolveMedia = useCallback(
    (mediaRef) => {
      if (!mediaRef) return '';
      if (typeof mediaRef === 'object' && mediaRef !== null) {
        const inner = mediaRef.image || mediaRef.url || mediaRef.file_path || mediaRef.src;
        return resolveMedia(inner);
      }
      const str = String(mediaRef).trim();
      if (!str) return '';
      if (
        str.startsWith('http://') ||
        str.startsWith('https://') ||
        str.startsWith('blob:') ||
        str.startsWith('data:')
      ) {
        return str;
      }
      const mapped = mediaMap[str] || mediaMap[Number(str)];
      if (mapped) return mapped;
      const resolved = getImageUrl(str);
      if (resolved) {
        if (
          resolved.startsWith('http://') ||
          resolved.startsWith('https://') ||
          resolved.startsWith('blob:') ||
          resolved.startsWith('data:')
        ) {
          return resolved;
        }
        return `${API_ORIGIN}${resolved.startsWith('/') ? '' : '/'}${resolved}`;
      }
      if (str.startsWith('/')) {
        return `${API_ORIGIN}${str}`;
      }
      if (/^\d+$/.test(str)) return '';
      return `${API_ORIGIN}/uploads/images/${str}`;
    },
    [mediaMap]
  );

  const parseList = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') return val.split('\n').map((s) => s.trim()).filter(Boolean);
    return [];
  };

  // 1. HERO SECTION DATA (careers-hero)
  const heroSec = liveSections?.['careers-hero'] || page?.sections?.['careers-hero'] || {};
  const heroKicker = heroSec?.kicker || page?.kicker || '';
  const heroTitle = heroSec?.title || page?.title || '';
  const heroSummary = heroSec?.summary || heroSec?.subtitle || page?.summary || page?.subtitle || '';

  const heroSlides = useMemo(() => {
    const rawSlides = heroSec?.slides || heroSec?.hero_slides || page?.hero_slides || page?.slides || [];
    if (!Array.isArray(rawSlides)) return [];

    return rawSlides
      .map((slide, idx) => {
        const rawUrl =
          typeof slide === 'object' && slide !== null
            ? slide.image || slide.url || slide.file_path || slide.src
            : slide;
        const url = resolveMedia(rawUrl);
        const isVideo = isVideoUrl(url);
        const youtubeId = isVideo ? extractYouTubeId(url) : null;
        return {
          id: slide?.id || idx,
          url,
          caption: slide?.caption || '',
          isVideo,
          isYouTube: Boolean(youtubeId),
          youtubeId,
        };
      })
      .filter((s) => Boolean(s.url));
  }, [heroSec?.slides, heroSec?.hero_slides, page?.hero_slides, page?.slides, resolveMedia]);

  // Carousel timer for hero
  useEffect(() => {
    if (!heroSlides.length) return;
    const intervalId = setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  // Scroll animations observer
  useEffect(() => {
    const animatedItems = document.querySelectorAll('.careers-animate');
    if (!animatedItems.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    animatedItems.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, [liveSections]);

  // 2. HIRING PHILOSOPHY DATA (careers-philosophy)
  const philosophySec = liveSections?.['careers-philosophy'] || page?.sections?.['careers-philosophy'] || {};
  const philosophyKicker = philosophySec?.kicker || page?.philosophy_kicker || '';
  const philosophyTitle = philosophySec?.title || page?.philosophy_title || '';
  const philosophyDesc = philosophySec?.description || page?.philosophy_description || '';
  const philosophyCta = philosophySec?.cta || page?.philosophy_cta || {};

  const hiringPhilosophy = useMemo(() => {
    const raw = philosophySec?.items || page?.hiring_philosophy || [];
    if (!Array.isArray(raw)) return [];

    return raw.map((item, index) => {
      const title = item?.title || '';
      const normalizedId = String(item?.id || title)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      return {
        id: normalizedId || `philosophy-${index + 1}`,
        title,
        description: item?.description || '',
        image: resolveMedia(item?.image),
        keyFocus: item?.key_focus || item?.keyFocus || item?.highlight || '',
      };
    });
  }, [philosophySec?.items, page?.hiring_philosophy, resolveMedia]);

  // 3. OPEN POSITIONS DATA (careers-positions)
  const positionsSec = liveSections?.['careers-positions'] || page?.sections?.['careers-positions'] || {};
  const positionsKicker = positionsSec?.kicker || page?.positions_kicker || '';
  const positionsTitle = positionsSec?.title || page?.positions_title || '';
  const positionsDesc = positionsSec?.description || page?.positions_description || '';

  const openPositions = useMemo(() => {
    const raw = positionsSec?.items || page?.open_positions || [];
    if (!Array.isArray(raw)) return [];

    return raw.map((item, index) => {
      const title = item?.title || '';
      const normalizedId = String(item?.id || title)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      return {
        id: normalizedId || `position-${index + 1}`,
        title,
        location: item?.location || 'Main Campus',
        type: item?.type || 'Full-time',
        status: item?.status || 'open',
        deadline: item?.deadline || '',
        summary: item?.summary || '',
        description: item?.description || '',
        responsibilities: parseList(item?.responsibilities || []),
        requirements: parseList(item?.requirements || []),
        applyNote: item?.applyNote || item?.apply_note || 'Contact HR for application details.',
      };
    });
  }, [positionsSec?.items, page?.open_positions]);

  // Helper function to calculate days until deadline
  const getDaysUntilDeadline = (deadline) => {
    if (!deadline) return null;
    const today = new Date();
    const deadlineDate = new Date(deadline);
    if (isNaN(deadlineDate.getTime())) return null;
    const diffTime = deadlineDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Helper function to get status badge
  const getStatusBadge = (status, deadline) => {
    const daysLeft = getDaysUntilDeadline(deadline);
    if (status === 'closed') {
      return {
        text: 'Closed',
        color: '#dc2626',
        bg: 'rgba(220, 38, 38, 0.12)',
        border: 'rgba(220, 38, 38, 0.28)',
      };
    }
    if (daysLeft !== null && daysLeft > 0 && daysLeft <= 7) {
      return {
        text: `${daysLeft} Days Left`,
        color: '#d97706',
        bg: 'rgba(217, 119, 6, 0.12)',
        border: 'rgba(217, 119, 6, 0.28)',
      };
    }
    return {
      text: 'Actively Hiring',
      color: '#059669',
      bg: 'rgba(5, 150, 105, 0.12)',
      border: 'rgba(5, 150, 105, 0.28)',
    };
  };

  // Filter only active positions
  const activePositions = useMemo(() => {
    const openOnly = openPositions.filter((pos) => pos.status !== 'closed');
    return openOnly.length > 0 ? openOnly : openPositions;
  }, [openPositions]);

  // 4. INTERNSHIPS & VOLUNTEERING DATA (careers-opportunities)
  const opportunitiesSec = liveSections?.['careers-opportunities'] || page?.sections?.['careers-opportunities'] || {};
  const opportunitiesKicker = opportunitiesSec?.kicker || page?.opportunities_kicker || '';
  const opportunitiesTitle = opportunitiesSec?.title || page?.opportunities_title || '';
  const opportunitiesDesc = opportunitiesSec?.description || page?.opportunities_description || '';

  const opportunities = useMemo(() => {
    const raw = opportunitiesSec?.items || page?.internships_volunteering || [];
    if (!Array.isArray(raw)) return [];

    return raw.map((item, index) => {
      const title = item?.title || '';
      const normalizedId = String(item?.id || title)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      return {
        id: normalizedId || `opportunity-${index + 1}`,
        title,
        description: item?.description || '',
        highlights: parseList(item?.highlights || []),
        fullDescription: item?.fullDescription || item?.description || '',
        duration: item?.duration || 'Flexible',
        commitment: item?.commitment || 'Varies',
        benefits: parseList(item?.benefits || []),
        requirements: parseList(item?.requirements || []),
        contact: item?.contact || 'hr@permataharapan.sch.id',
      };
    });
  }, [opportunitiesSec?.items, page?.internships_volunteering]);

  if (loading && !liveSections) {
    return (
      <section className="careers-page" style={{ padding: '60px 20px', maxWidth: '1200px', margin: '0 auto' }}>
        <div
          style={{
            height: '420px',
            borderRadius: '24px',
            background: 'linear-gradient(90deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
            backgroundSize: '200% 100%',
            animation: 'pulse 1.5s infinite',
            marginBottom: '48px',
          }}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
          <div style={{ height: '240px', borderRadius: '16px', background: '#e2e8f0', animation: 'pulse 1.5s infinite' }} />
          <div style={{ height: '240px', borderRadius: '16px', background: '#e2e8f0', animation: 'pulse 1.5s infinite' }} />
          <div style={{ height: '240px', borderRadius: '16px', background: '#e2e8f0', animation: 'pulse 1.5s infinite' }} />
        </div>
      </section>
    );
  }

  return (
    <section className="careers-page">
      {/* 1. HERO SECTION (Exact Match to About Us Hero) */}
      <section className="careers-hero" id="careers-overview">
        <div className="careers-hero-media" aria-hidden="true">
          {heroSlides.length > 0 ? (
            heroSlides.map((slide, index) => {
              const isCurrent = index === activeSlide;
              return (
                <div
                  key={slide.id || index}
                  className={`careers-hero-slide ${isCurrent ? 'active' : ''}`}
                >
                  {slide.isVideo ? (
                    slide.isYouTube ? (
                      <iframe
                        className="careers-hero-video-frame"
                        src={`https://www.youtube.com/embed/${slide.youtubeId}?autoplay=1&mute=1&loop=1&playlist=${slide.youtubeId}&controls=0&playsinline=1&modestbranding=1&rel=0`}
                        title="Careers Hero video background"
                        allow="autoplay; encrypted-media"
                        tabIndex="-1"
                      />
                    ) : (
                      <video
                        className="careers-hero-video-element"
                        src={slide.url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                      />
                    )
                  ) : (
                    <img
                      src={slide.url}
                      alt={heroTitle || 'Careers'}
                      className="careers-hero-img-element"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                </div>
              );
            })
          ) : (
            <div className="careers-hero-slide active careers-hero-gradient-fallback" />
          )}
        </div>

        <div className="careers-hero-overlay" aria-hidden="true" />

        <div className="careers-hero-content-wrap">
          <div className="careers-hero-content">
            {heroKicker && (
              <p className="careers-hero-kicker">
                {heroKicker}
              </p>
            )}

            {heroTitle && (
              <h1 className="careers-hero-title">
                {heroTitle}
              </h1>
            )}

            {heroSummary && (
              <p className="careers-hero-summary" style={{ whiteSpace: 'pre-line' }}>
                {heroSummary}
              </p>
            )}

            {heroSlides.length > 1 && (
              <div className="careers-hero-dots" role="tablist" aria-label="Careers hero carousel navigation">
                {heroSlides.map((slide, index) => (
                  <button
                    key={slide.id || index}
                    type="button"
                    className={`careers-hero-dot ${index === activeSlide ? 'active' : ''}`}
                    onClick={() => setActiveSlide(index)}
                    aria-label={`Show slide ${index + 1}`}
                    role="tab"
                    aria-selected={index === activeSlide}
                  >
                    <span className="careers-hero-dot-bar" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 2. PHILOSOPHY & JOIN OUR TEAM */}
      {(philosophyTitle || hiringPhilosophy.length > 0) && (
        <section className="careers-join" id="join-our-team">
          <div className="careers-join-wrapper">
            <div className="careers-join-header careers-animate">
              {philosophyKicker && (
                <div className="careers-join-kicker">
                  <span className="careers-kicker-dot" />
                  <span>{philosophyKicker}</span>
                </div>
              )}
              {philosophyTitle && <h2>{philosophyTitle}</h2>}
              {philosophyDesc && <p style={{ whiteSpace: 'pre-line' }}>{philosophyDesc}</p>}
            </div>

            {hiringPhilosophy.length > 0 && (
              <div className="careers-philosophy-grid careers-animate">
                {hiringPhilosophy.map((item, index) => {
                  const icons = [
                    <Heart size={26} />,
                    <GraduationCap size={26} />,
                    <Handshake size={26} />,
                  ];
                  const icon = icons[index % icons.length];
                  return (
                    <article key={item.id} id={item.id} className="philosophy-card">
                      <div className="philosophy-card-accent" />

                      {item.image && (
                        <div className="philosophy-card-media">
                          <img
                            src={item.image}
                            alt={item.title}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                          <div className="philosophy-media-overlay" />
                        </div>
                      )}

                      <div className="philosophy-card-body">
                        <div className="philosophy-card-top">
                          <div className="philosophy-icon-wrap">{icon}</div>
                          <span className="philosophy-index-badge">0{index + 1}</span>
                        </div>

                        <h3 className="philosophy-title">{item.title}</h3>
                        <p className="philosophy-desc" style={{ whiteSpace: 'pre-line' }}>{item.description}</p>

                        {item.keyFocus && (
                          <div className="philosophy-highlight-pill">
                            <Star size={14} />
                            <div className="philosophy-highlight-text">
                              <span className="highlight-label">Key Focus</span>
                              <span className="highlight-val">{item.keyFocus}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}

            {(philosophyCta.title || philosophyCta.description) && (
              <div className="careers-join-cta careers-animate">
                <div className="careers-join-cta-glow" />
                <div className="careers-join-cta-content">
                  {philosophyCta.title && <h3>{philosophyCta.title}</h3>}
                  {philosophyCta.description && <p style={{ whiteSpace: 'pre-line' }}>{philosophyCta.description}</p>}
                  <a
                    href={philosophyCta.button_url || philosophyCta.buttonLink || '#open-positions'}
                    className="careers-join-cta-button"
                  >
                    <span>{philosophyCta.button_label || philosophyCta.buttonText || 'Explore Open Positions'}</span>
                    <ArrowRight size={18} />
                  </a>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* 3. OPEN POSITIONS */}
      {(positionsTitle || openPositions.length > 0) && (
        <section className="careers-open" id="open-positions">
          <div className="careers-open-glow-1" />
          <div className="careers-open-glow-2" />
          <div className="careers-open-wrapper">
            <div className="careers-open-header careers-animate">
              {positionsKicker && (
                <div className="careers-open-kicker">
                  <span className="careers-kicker-dot" />
                  <span>{positionsKicker}</span>
                </div>
              )}
              {positionsTitle && <h2>{positionsTitle}</h2>}
              {positionsDesc && <p style={{ whiteSpace: 'pre-line' }}>{positionsDesc}</p>}
              {activePositions.length > 0 && (
                <div className="careers-open-count-badge">
                  <Sparkles size={14} />
                  <span>{activePositions.length} Open Roles Available</span>
                </div>
              )}
            </div>

            {activePositions.length > 0 ? (
              <div className="careers-open-list careers-animate">
                {activePositions.map((role) => {
                  const statusInfo = getStatusBadge(role.status, role.deadline);

                  return (
                    <article
                      key={role.id}
                      id={role.id}
                      className="careers-open-card"
                      onClick={() => setSelectedPosition(role)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setSelectedPosition(role);
                        }
                      }}
                    >
                      <div className="careers-open-card-accent-bar" />

                      <div className="careers-open-card-top">
                        <div className="careers-open-card-title-group">
                          <div className="careers-open-role-icon">
                            <Briefcase size={22} />
                          </div>
                          <div className="careers-open-role-heading">
                            <h3 className="careers-open-card-title">{role.title}</h3>
                            {role.summary && <p className="careers-open-card-summary" style={{ whiteSpace: 'pre-line' }}>{role.summary}</p>}
                          </div>
                        </div>

                        <div className="careers-open-badge-group">
                          <span
                            className="careers-open-status-pill"
                            style={{
                              color: statusInfo.color,
                              backgroundColor: statusInfo.bg,
                              borderColor: statusInfo.border,
                            }}
                          >
                            <span
                              className="careers-open-status-dot"
                              style={{ backgroundColor: statusInfo.color }}
                            />
                            {statusInfo.text}
                          </span>
                        </div>
                      </div>

                      <div className="careers-open-meta-strip">
                        {role.location && (
                          <div className="careers-open-meta-item">
                            <MapPin size={15} />
                            <span>{role.location}</span>
                          </div>
                        )}
                        {role.type && (
                          <div className="careers-open-meta-item">
                            <Clock size={15} />
                            <span>{role.type}</span>
                          </div>
                        )}
                        {role.deadline && (
                          <div className="careers-open-meta-item">
                            <Calendar size={15} />
                            <span>
                              Apply by{' '}
                              {new Date(role.deadline).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="careers-open-card-bottom">
                        {role.description && (
                          <p className="careers-open-card-desc" style={{ whiteSpace: 'pre-line' }}>{role.description}</p>
                        )}
                        <div className="careers-open-action-btn">
                          <span>View Role Details & Apply</span>
                          <ArrowRight size={16} />
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="careers-open-empty">
                <Briefcase size={36} />
                <h3>No Open Positions</h3>
                <p>There are currently no open positions. Please check back soon!</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Position Detail Modal */}
      {selectedPosition && (
        <div className="careers-position-modal-overlay" onClick={() => setSelectedPosition(null)}>
          <div className="careers-position-modal" onClick={(e) => e.stopPropagation()}>
            <div className="careers-modal-top-accent" />

            {/* Modal Hero Banner */}
            <div className="careers-modal-hero">
              <div className="careers-modal-hero-top">
                <div className="careers-modal-kicker">
                  <span className="careers-kicker-dot" />
                  <span>Career Opportunity</span>
                </div>
                <button
                  className="careers-modal-close"
                  onClick={() => setSelectedPosition(null)}
                  type="button"
                  aria-label="Close modal"
                >
                  <X size={20} />
                </button>
              </div>

              <h2 className="careers-modal-hero-title">{selectedPosition.title}</h2>
              {selectedPosition.summary && (
                <p className="careers-modal-hero-summary" style={{ whiteSpace: 'pre-line' }}>{selectedPosition.summary}</p>
              )}

              {/* Header Badges Ribbon */}
              <div className="careers-modal-hero-ribbon">
                {selectedPosition.location && (
                  <div className="careers-modal-hero-pill">
                    <MapPin size={15} />
                    <span>{selectedPosition.location}</span>
                  </div>
                )}
                {selectedPosition.type && (
                  <div className="careers-modal-hero-pill">
                    <Clock size={15} />
                    <span>{selectedPosition.type}</span>
                  </div>
                )}
                {selectedPosition.deadline && (
                  <div className="careers-modal-hero-pill">
                    <Calendar size={15} />
                    <span>
                      Apply by{' '}
                      {new Date(selectedPosition.deadline).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Scrollable Content */}
            <div className="careers-modal-body">
              {selectedPosition.description && (
                <div className="careers-modal-card-block">
                  <div className="careers-modal-block-heading">
                    <div className="careers-modal-heading-icon">
                      <Briefcase size={18} />
                    </div>
                    <h3>About This Role</h3>
                  </div>
                  <p className="careers-modal-text" style={{ whiteSpace: 'pre-line' }}>{selectedPosition.description}</p>
                </div>
              )}

              {Array.isArray(selectedPosition.responsibilities) && selectedPosition.responsibilities.length > 0 && (
                <div className="careers-modal-card-block">
                  <div className="careers-modal-block-heading">
                    <div className="careers-modal-heading-icon">
                      <CheckCircle2 size={18} />
                    </div>
                    <h3>Key Responsibilities</h3>
                  </div>
                  <ul className="careers-modal-grid-list">
                    {selectedPosition.responsibilities.map((item, idx) => (
                      <li key={idx} className="careers-modal-list-item">
                        <div className="careers-modal-item-bullet">
                          <CheckCircle2 size={15} />
                        </div>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {Array.isArray(selectedPosition.requirements) && selectedPosition.requirements.length > 0 && (
                <div className="careers-modal-card-block">
                  <div className="careers-modal-block-heading">
                    <div className="careers-modal-heading-icon star">
                      <Star size={18} />
                    </div>
                    <h3>Requirements & Qualifications</h3>
                  </div>
                  <ul className="careers-modal-grid-list">
                    {selectedPosition.requirements.map((item, idx) => (
                      <li key={idx} className="careers-modal-list-item">
                        <div className="careers-modal-item-bullet star">
                          <Star size={15} />
                        </div>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedPosition.applyNote && (
                <div className="careers-modal-apply-banner">
                  <div className="careers-modal-apply-icon">
                    <Mail size={24} />
                  </div>
                  <div className="careers-modal-apply-info">
                    <h4>How to Apply</h4>
                    <p style={{ whiteSpace: 'pre-line' }}>{selectedPosition.applyNote}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Docked Footer */}
            <div className="careers-modal-footer">
              <button
                type="button"
                className="careers-modal-close-btn"
                onClick={() => setSelectedPosition(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="careers-modal-apply-btn"
                onClick={() => {
                  window.location.href = `mailto:hr@permataharapan.sch.id?subject=Application for ${selectedPosition.title}`;
                }}
              >
                <span>Apply via Email</span>
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 4. INTERNSHIPS & VOLUNTEERING */}
      {(opportunitiesTitle || opportunities.length > 0) && (
        <section className="careers-opportunities" id="internships-volunteering">
          <div className="careers-opportunities-glow-1" />
          <div className="careers-opportunities-glow-2" />
          <div className="careers-opportunities-wrapper">
            <div className="careers-opportunities-header careers-animate">
              {opportunitiesKicker && (
                <div className="careers-opportunities-kicker">
                  <span className="careers-kicker-dot" />
                  <span>{opportunitiesKicker}</span>
                </div>
              )}
              {opportunitiesTitle && <h2>{opportunitiesTitle}</h2>}
              {opportunitiesDesc && <p style={{ whiteSpace: 'pre-line' }}>{opportunitiesDesc}</p>}
            </div>

            {opportunities.length > 0 && (
              <div className="careers-opportunities-grid careers-animate">
                {opportunities.map((item, index) => {
                  const icons = [<GraduationCap size={24} />, <Handshake size={24} />, <Sparkles size={24} />];
                  const icon = icons[index % icons.length];
                  const badges = ['Internship Track', 'Community Service', 'Research & Innovation'];
                  const categoryBadge = badges[index % badges.length];

                  return (
                    <article
                      key={item.id}
                      id={item.id}
                      className="careers-opportunity-card"
                      onClick={() => setSelectedOpportunity(item)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setSelectedOpportunity(item);
                        }
                      }}
                    >
                      <div className="careers-opportunity-accent-bar" />

                      <div className="careers-opportunity-card-top">
                        <div className="careers-opportunity-icon-box">
                          {icon}
                        </div>
                        <span className="careers-opportunity-badge">{categoryBadge}</span>
                      </div>

                      <div className="careers-opportunity-body">
                        <h3 className="careers-opportunity-title">{item.title}</h3>
                        <p className="careers-opportunity-desc" style={{ whiteSpace: 'pre-line' }}>{item.description}</p>
                      </div>

                      {Array.isArray(item.highlights) && item.highlights.length > 0 && (
                        <div className="careers-opportunity-highlights">
                          <span className="opportunity-highlights-title">Key Program Highlights:</span>
                          <div className="opportunity-highlights-list">
                            {item.highlights.map((highlight, idx) => (
                              <div key={idx} className="opportunity-highlight-item">
                                <CheckCircle2 size={15} />
                                <span>{highlight}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="careers-opportunity-meta-strip">
                        {item.duration && (
                          <div className="opportunity-meta-pill">
                            <Hourglass size={13} />
                            <span>{item.duration}</span>
                          </div>
                        )}
                        {item.commitment && (
                          <div className="opportunity-meta-pill">
                            <Clock size={13} />
                            <span>{item.commitment}</span>
                          </div>
                        )}
                      </div>

                      <div className="careers-opportunity-footer">
                        <div className="careers-opportunity-cta">
                          <span>Explore Details & Apply</span>
                          <ArrowRight size={16} />
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Opportunity Detail Modal */}
      {selectedOpportunity && (
        <div className="careers-opportunity-modal-overlay" onClick={() => setSelectedOpportunity(null)}>
          <div className="careers-opportunity-modal" onClick={(e) => e.stopPropagation()}>
            <div className="careers-modal-top-accent" />

            {/* Modal Hero Banner */}
            <div className="careers-modal-hero">
              <div className="careers-modal-hero-top">
                <div className="careers-modal-kicker">
                  <span className="careers-kicker-dot" />
                  <span>Program Opportunity</span>
                </div>
                <button
                  className="careers-opportunity-modal-close"
                  onClick={() => setSelectedOpportunity(null)}
                  type="button"
                  aria-label="Close modal"
                >
                  <X size={20} />
                </button>
              </div>

              <h2 className="careers-modal-hero-title">{selectedOpportunity.title}</h2>
              {selectedOpportunity.description && (
                <p className="careers-modal-hero-summary" style={{ whiteSpace: 'pre-line' }}>{selectedOpportunity.description}</p>
              )}

              {/* Header Badges Ribbon */}
              <div className="careers-modal-hero-ribbon">
                {selectedOpportunity.duration && (
                  <div className="careers-modal-hero-pill">
                    <Hourglass size={15} />
                    <span>Duration: {selectedOpportunity.duration}</span>
                  </div>
                )}
                {selectedOpportunity.commitment && (
                  <div className="careers-modal-hero-pill">
                    <Clock size={15} />
                    <span>Commitment: {selectedOpportunity.commitment}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Scrollable Content */}
            <div className="careers-modal-body">
              {selectedOpportunity.fullDescription && (
                <div className="careers-modal-card-block">
                  <div className="careers-modal-block-heading">
                    <div className="careers-modal-heading-icon">
                      <GraduationCap size={18} />
                    </div>
                    <h3>About This Opportunity</h3>
                  </div>
                  <p className="careers-modal-text" style={{ whiteSpace: 'pre-line' }}>{selectedOpportunity.fullDescription}</p>
                </div>
              )}

              {Array.isArray(selectedOpportunity.benefits) && selectedOpportunity.benefits.length > 0 && (
                <div className="careers-modal-card-block">
                  <div className="careers-modal-block-heading">
                    <div className="careers-modal-heading-icon star">
                      <Star size={18} />
                    </div>
                    <h3>What You'll Gain</h3>
                  </div>
                  <ul className="careers-modal-grid-list">
                    {selectedOpportunity.benefits.map((benefit, idx) => (
                      <li key={idx} className="careers-modal-list-item">
                        <div className="careers-modal-item-bullet star">
                          <Star size={15} />
                        </div>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {Array.isArray(selectedOpportunity.requirements) && selectedOpportunity.requirements.length > 0 && (
                <div className="careers-modal-card-block">
                  <div className="careers-modal-block-heading">
                    <div className="careers-modal-heading-icon">
                      <CheckCircle2 size={18} />
                    </div>
                    <h3>What We're Looking For</h3>
                  </div>
                  <ul className="careers-modal-grid-list">
                    {selectedOpportunity.requirements.map((req, idx) => (
                      <li key={idx} className="careers-modal-list-item">
                        <div className="careers-modal-item-bullet">
                          <CheckCircle2 size={15} />
                        </div>
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedOpportunity.contact && (
                <div className="careers-modal-apply-banner">
                  <div className="careers-modal-apply-icon">
                    <Mail size={24} />
                  </div>
                  <div className="careers-modal-apply-info">
                    <h4>Direct Contact & Inquiries</h4>
                    <p>Send your CV or expression of interest to {selectedOpportunity.contact}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Docked Footer */}
            <div className="careers-modal-footer">
              <button
                type="button"
                className="opportunity-modal-close-btn"
                onClick={() => setSelectedOpportunity(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="opportunity-modal-apply-btn"
                onClick={() => {
                  window.location.href = `mailto:${selectedOpportunity.contact}?subject=Interest in ${selectedOpportunity.title}`;
                }}
              >
                <span>Express Interest</span>
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default CareersPage;
